import React, { useState, useEffect } from "react";
import {
  Modal,
  Select,
  InputNumber,
  Button,
  Table,
  message,
  Divider,
  Typography,
  Row,
  Col,
} from "antd";
import { platformApi } from "../../../../api";

const { Option } = Select;
const { Text, Title } = Typography;

const AddDepositModal = ({ open, close, branchOptions, emitData }) => {
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [denominations, setDenominations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromCounts, setFromCounts] = useState({});
  const [toCounts, setToCounts] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Define standard denominations for "To" section if not available in inventory
  const standardDenominations = [2000, 500, 200, 100, 50, 20, 10, 5, 2, 1];

  useEffect(() => {
    if (open) {
      const defaultBranch = branchOptions?.[0]?.id || null;
      setSelectedBranch(defaultBranch);
      setFromCounts({});
      setToCounts({});
      setDenominations([]);
      if (defaultBranch) {
        fetchDenominations(defaultBranch);
      }
    }
  }, [open, branchOptions]);

  const fetchDenominations = (branchId) => {
    setLoading(true);
    platformApi
      .post("/api/payment/denominationInventory", { branch: [branchId] })
      .then((res) => {
        setLoading(false);
        if (res.data.code === 200 && res.data.response.code === 200) {
          const sortedData = (res.data.response.data[0]?.denominations || []).sort(
            (a, b) => b.denomination - a.denomination
          );
          setDenominations(sortedData || []);
        } else {
          message.error("Failed to fetch denominations");
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
        message.error("Error fetching denominations");
      });
  };

  const handleBranchChange = (val) => {
    setSelectedBranch(val);
    setFromCounts({});
    setToCounts({});
    fetchDenominations(val);
  };

  const handleCountChange = (type, denomination, val, available) => {
    if (type === "from" && val > available) {
      message.warning(
        `Cannot exceed available count (${available}) for ₹${denomination}`
      );
      return;
    }
    const setter = type === "from" ? setFromCounts : setToCounts;
    setter((prev) => ({
      ...prev,
      [denomination]: val,
    }));
  };

  const calculateTotal = (counts) => {
    return Object.entries(counts).reduce((acc, [denom, count]) => {
      return acc + Number(denom) * (count || 0);
    }, 0);
  };

  const totalFrom = calculateTotal(fromCounts);
  const totalTo = calculateTotal(toCounts);

  const handleSubmit = () => {
    if (!selectedBranch) {
      return message.warning("Please select a branch");
    }

    if (totalFrom === 0 || totalTo === 0) {
      return message.warning("Please enter at least one denomination in both sections");
    }

    if (totalFrom !== totalTo) {
      return message.error(
        `Total amounts must match! (Reduce: ₹${totalFrom.toLocaleString()} != Add: ₹${totalTo.toLocaleString()})`
      );
    }

    const payload = {
      branchId: selectedBranch,
      fromDenominations: Object.entries(fromCounts)
        .filter(([_, count]) => count > 0)
        .map(([denom, count]) => ({
          denomination: Number(denom),
          count,
        })),
      toDenominations: Object.entries(toCounts)
        .filter(([_, count]) => count > 0)
        .map(([denom, count]) => ({
          denomination: Number(denom),
          count,
        })),
    };

    setSubmitting(true);
    emitData(payload)
      .then(() => {
        setSubmitting(false);
        close();
      })
      .catch(() => {
        setSubmitting(false);
      });
  };

  const fromColumns = [
    {
      title: "Denom",
      dataIndex: "denomination",
      key: "denomination",
      render: (text) => <span>₹{text}</span>,
    },
    {
      title: "Stock",
      dataIndex: "count",
      key: "count",
    },
    {
      title: "Qty",
      key: "qty",
      render: (_, record) => (
        <InputNumber
          size="small"
          min={0}
          max={record.count}
          value={fromCounts[record.denomination] || 0}
          onChange={(val) =>
            handleCountChange("from", record.denomination, val, record.count)
          }
        />
      ),
    },
  ];

  const toColumns = [
    {
      title: "Denom",
      dataIndex: "denomination",
      key: "denomination",
      render: (text) => <span>₹{text}</span>,
    },
    {
      title: "Qty",
      key: "qty",
      render: (_, record) => (
        <InputNumber
          size="small"
          min={0}
          value={toCounts[record.denomination] || 0}
          onChange={(val) => handleCountChange("to", record.denomination, val)}
        />
      ),
    },
  ];

  // For the "Add To" table, we ensure all standard denominations are available to select
  const toData = standardDenominations.map((denom) => ({
    denomination: denom,
  }));

  return (
    <Modal
  title="Denomination Exchange (Deposit)"
  visible={open}
  onCancel={close}
  width={900}
  footer={[
    <Button key="close" onClick={close}>
      Close
    </Button>,
    <Button
      key="submit"
      type="primary"
      loading={submitting}
      onClick={handleSubmit}
      disabled={totalFrom === 0 || totalFrom !== totalTo}
    >
      Exchange Denominations
    </Button>,
  ]}
>
  {/* ✅ Branch Row — aligned horizontally */}
  <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
    <Text strong style={{ fontSize: 16, whiteSpace: "nowrap" }}>
      Branch:
    </Text>
    <Select
      style={{ width: 300, marginLeft: 10 }}
      placeholder="Select Branch"
      value={selectedBranch}
      onChange={handleBranchChange}
      disabled={loading}
    >
      {branchOptions?.map((b) => (
        <Option key={b.id} value={b.id}>
          {b.name}
        </Option>
      ))}
    </Select>
  </div>

  {/* ✅ Tables Section */}
  <Row gutter={24}>
    <Col span={12}>
      {/* Title above table */}
      <div style={{ fontWeight: "bold", fontSize: 16, marginBottom: 8 }}>
        Reduce From (Release)
      </div>
      <Table
        dataSource={denominations}
        columns={fromColumns}
        rowKey="denomination"
        pagination={false}
        loading={loading}
      />
      <div style={{ marginTop: 10, textAlign: "right" }}>
        <Text strong>Subtotal From: ₹{totalFrom.toLocaleString()}</Text>
      </div>
    </Col>

    <Col span={12}>
      <div style={{ fontWeight: "bold", fontSize: 16, marginBottom: 8 }}>
        Add To (Receive)
      </div>
      <Table
        dataSource={toData}
        columns={toColumns}
        rowKey="denomination"
        pagination={false}
      />
      <div style={{ marginTop: 10, textAlign: "right" }}>
        <Text strong>Subtotal To: ₹{totalTo.toLocaleString()}</Text>
      </div>
    </Col>
  </Row>

  <Divider />

  {/* ✅ Total Section */}
  <div style={{ textAlign: "center" }}>
    {totalFrom !== totalTo && totalFrom > 0 && totalTo > 0 ? (
      <Text type="danger">
        Mismatch: Dif ₹{(totalFrom - totalTo).toLocaleString()}
      </Text>
    ) : (
      <Title level={4}>Total Balance: ₹{totalFrom.toLocaleString()}</Title>
    )}
  </div>
</Modal>

  );
};

export default AddDepositModal;
